import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import { useAuth } from "../lib/auth-context";

const ROLES = [
    "ORG_ADMIN",
    "LD_ADMIN",
    "CONTENT_CREATOR",
    "SME_REVIEWER",
    "LEARNER",
    "AUDITOR",
];

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", password: "", role: "LEARNER" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/users", form);
            toast.success("User created");
            setOpen(false);
            setForm({ email: "", name: "", password: "", role: "LEARNER" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const changeRole = async (id, role) => {
        try {
            await api.patch(`/users/${id}`, { role });
            toast.success("Role updated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deactivate = async (id) => {
        try {
            await api.post(`/users/${id}/deactivate`);
            toast.success("User deactivated");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="users-page" className="space-y-8">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                        Admin
                    </div>
                    <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                        Users
                    </h1>
                    <p className="mt-1.5 text-sm text-neutral-500">
                        Invite, adjust roles, and deactivate accounts within your organisation.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            data-testid="create-user-btn"
                            className="bg-neutral-900 text-white hover:bg-neutral-800"
                        >
                            <Plus size={15} className="mr-1.5" />
                            New user
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="create-user-dialog" className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Add user</DialogTitle>
                            <DialogDescription>
                                Create a new member for {"your organisation"}. They can sign in with
                                the temporary password below.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label className="text-xs">Full name</Label>
                                <Input
                                    data-testid="user-form-name"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Email</Label>
                                <Input
                                    data-testid="user-form-email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Temporary password</Label>
                                <Input
                                    data-testid="user-form-password"
                                    type="text"
                                    minLength={8}
                                    required
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                />
                                <div className="text-[11px] text-neutral-500">
                                    At least 8 characters. Share securely with the user.
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs">Role</Label>
                                <Select
                                    value={form.role}
                                    onValueChange={(v) => setForm({ ...form, role: v })}
                                >
                                    <SelectTrigger data-testid="user-form-role">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => (
                                            <SelectItem key={r} value={r}>
                                                {r}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    data-testid="user-form-submit"
                                    disabled={busy}
                                    className="bg-neutral-900 text-white hover:bg-neutral-800"
                                >
                                    {busy ? "Creating…" : "Create user"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody data-testid="users-table-body">
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-sm text-neutral-500">
                                    Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            users.map((u) => (
                                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                                    <TableCell className="font-medium">{u.name}</TableCell>
                                    <TableCell className="text-neutral-600">{u.email}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={u.role}
                                            onValueChange={(v) => changeRole(u.id, v)}
                                            disabled={u.id === me?.id}
                                        >
                                            <SelectTrigger
                                                data-testid={`role-select-${u.email}`}
                                                className="h-8 w-44"
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ROLES.map((r) => (
                                                    <SelectItem key={r} value={r}>
                                                        {r}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <span
                                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${
                                                u.is_active
                                                    ? "bg-emerald-50 text-emerald-700"
                                                    : "bg-neutral-100 text-neutral-500"
                                            }`}
                                        >
                                            {u.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {u.is_active && u.id !== me?.id && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`deactivate-btn-${u.email}`}
                                                onClick={() => deactivate(u.id)}
                                                className="text-neutral-600 hover:text-red-600"
                                            >
                                                Deactivate
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
